import { FingerprintIcon } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input, Label } from "@/components/ui/input";
import { authClient, authErrorCode } from "../auth";

/** The frame of the pages before signing in: the name and one card. */
export function SignedOutCard({ title, children }: { title: string; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <main className="flex min-h-dvh items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-center gap-2">
          <img src="/favicon.svg" alt="" className="size-8" />
          <span className="text-lg font-semibold tracking-tight">{t("adminApp.title")}</span>
        </div>
        <section className="rounded-2xl border border-border bg-surface p-6 shadow-sm">
          <h1 className="text-lg font-semibold tracking-tight">{title}</h1>
          {children}
        </section>
      </div>
    </main>
  );
}

/** After signing in the page loads anew, so the session is read from the start. */
const enter = () => window.location.assign("/");

export function SignInPage() {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // The browser offers the passkey in the field's suggestions, where it can (conditional UI).
  useEffect(() => {
    let active = true;
    void (async () => {
      const conditional = await PublicKeyCredential?.isConditionalMediationAvailable?.();
      if (!conditional || !active) return;
      const { data } = await authClient.signIn.passkey({ autoFill: true });
      if (data && active) enter();
    })();
    return () => {
      active = false;
    };
  }, []);

  const signIn = async () => {
    setBusy(true);
    setFailed(false);
    const { data } = await authClient.signIn.passkey();
    if (data) return enter();
    setBusy(false);
    setFailed(true);
  };

  return (
    <SignedOutCard title={t("adminApp.signIn.title")}>
      <p className="mt-1 text-sm text-muted-foreground">{t("adminApp.signIn.body")}</p>
      <Label htmlFor="operator-email" className="mt-5 block">
        {t("adminApp.signIn.email")}
      </Label>
      <Input
        id="operator-email"
        name="email"
        className="mt-1.5"
        autoComplete="username webauthn"
        inputMode="email"
        placeholder={t("adminApp.signIn.emailHint")}
      />
      <Button className="mt-4 w-full" variant="brand" disabled={busy} onClick={signIn}>
        <FingerprintIcon />
        {t("adminApp.signIn.button")}
      </Button>
      {failed ? (
        <p role="alert" className="mt-3 text-sm text-missing-fg">
          {t("adminApp.passkeyFailed")}
        </p>
      ) : null}
    </SignedOutCard>
  );
}

export function RegisterPage({ token }: { token: string }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const register = async () => {
    setBusy(true);
    setError(null);
    const { data, error: failure } = await authClient.passkey.addPasskey({
      context: token,
      createSession: true,
      name: t("adminApp.passkeys.firstName"),
    });
    if (data) return enter();
    setBusy(false);
    setError(
      authErrorCode(failure) === "OPERATOR_LINK_INVALID"
        ? t("errors.OPERATOR_LINK_INVALID")
        : t("adminApp.passkeyFailed"),
    );
  };

  return (
    <SignedOutCard title={t("adminApp.register.title")}>
      <p className="mt-1 text-sm text-muted-foreground">{t("adminApp.register.body")}</p>
      <Button className="mt-5 w-full" variant="brand" disabled={busy} onClick={register}>
        <FingerprintIcon />
        {t("adminApp.register.button")}
      </Button>
      {error ? (
        <p role="alert" className="mt-3 text-sm text-missing-fg">
          {error}
        </p>
      ) : null}
    </SignedOutCard>
  );
}
